import { useEffect, useRef } from 'react'
import { createConsumer } from '@rails/actioncable'

// One websocket per tab. Every subscription rides on it; creating a consumer
// per component opened a socket per component.
let consumer = null
export const getConsumer = () => {
  if (!consumer) { consumer = createConsumer() }
  return consumer
}

// One subscription per channel, shared by every component listening to it.
// Two subscriptions with the same identifier don't work: the server ignores
// the second subscribe without confirming it, and the client then retries it
// every 500ms for as long as it lives. { key: { subscription, handlers } }
const shared = new Map()

// Listen to a channel for as long as the component is mounted. The handler is
// read through a ref so a re-render doesn't tear anything down.
export const useCableChannel = (channel, onReceived, enabled = true) => {
  const handler = useRef(onReceived)
  handler.current = onReceived

  useEffect(() => {
    if (!enabled) { return }
    const key = JSON.stringify(channel)
    let entry = shared.get(key)
    if (!entry) {
      const handlers = new Set()
      const subscription = getConsumer().subscriptions.create(channel, {
        received: (data) => handlers.forEach(h => h(data))
      })
      entry = { subscription, handlers }
      shared.set(key, entry)
    }
    const listener = (data) => handler.current(data)
    entry.handlers.add(listener)

    return () => {
      entry.handlers.delete(listener)
      if (entry.handlers.size > 0) { return }
      entry.subscription.unsubscribe()
      shared.delete(key)
    }
  }, [JSON.stringify(channel), enabled])
}
